import React from 'react'
import Card from '../../../components/base/Card'

function ShirtsSalesTab({ salesData }) {

  const renderBarChart = () => {
    const dailyData = salesData?.shirts?.dailyData || [];
    if (dailyData.length === 0) {
      return (
        <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
          No data for selected period
        </div>
      );
    }

    const maxSales = Math.max(...dailyData.map(d => d.sales), 1);
    const chartWidth = 600;
    const chartHeight = 160;
    const paddingLeft = 50;
    const paddingRight = 20;
    const usableWidth = chartWidth - paddingLeft - paddingRight;

    const barWidth = Math.max(6, Math.min(24, usableWidth / dailyData.length - 6));
    const groupWidth = usableWidth / dailyData.length;
    const xForBar = (i) => paddingLeft + i * groupWidth + (groupWidth - barWidth) / 2;
    const yFor = (value) => chartHeight - (value / maxSales) * chartHeight;
    const hFor = (value) => (value / maxSales) * chartHeight;

    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium text-sm text-gray-700">Daily Sales (Revenue)</h4>
          <div className="flex items-center gap-1 text-xs">
            <div className="w-3 h-3 rounded-sm bg-green-500" />
            <span>Shirts Revenue</span>
          </div>
        </div>
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight + 30}`} className="w-full" style={{ height: 220 }}>
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
            <g key={i}>
              <line x1={paddingLeft} y1={yFor(maxSales * pct)} x2={chartWidth - paddingRight} y2={yFor(maxSales * pct)} stroke="#E5E7EB" strokeWidth="1" />
              <text x={paddingLeft - 4} y={yFor(maxSales * pct) + 4} textAnchor="end" fontSize="9" fill="#9CA3AF">
                ₹{Math.round(maxSales * pct / 1000)}k
              </text>
            </g>
          ))}

          {/* Bars */}
          {dailyData.map((d, i) => (
            <g key={i}>
              <rect
                x={xForBar(i)}
                y={yFor(d.sales)}
                width={barWidth}
                height={hFor(d.sales)}
                fill="#10B981"
                rx="2"
                opacity="0.85"
              >
                <title>{d.date}: ₹{d.sales?.toLocaleString()} | {d.pieces} Pcs</title>
              </rect>
              <text x={xForBar(i) + barWidth / 2} y={chartHeight + 18} textAnchor="middle" fontSize="9" fill="#6B7280">
                {new Date(d.date).getDate()}/{new Date(d.date).getMonth() + 1}
              </text>
            </g>
          ))}

          {/* Baseline */}
          <line x1={paddingLeft} y1={chartHeight} x2={chartWidth - paddingRight} y2={chartHeight} stroke="#D1D5DB" strokeWidth="1" />
        </svg>
      </div>
    );
  };

  const renderPiecesChart = () => {
    const dailyData = salesData?.shirts?.dailyData || [];
    if (dailyData.length === 0) return null;

    const maxPieces = Math.max(...dailyData.map(d => d.pieces), 1);
    const chartWidth = 600;
    const chartHeight = 120;
    const paddingLeft = 50;
    const paddingRight = 20;
    const usableWidth = chartWidth - paddingLeft - paddingRight;

    const barWidth = Math.max(6, Math.min(24, usableWidth / dailyData.length - 6));
    const groupWidth = usableWidth / dailyData.length;
    const xForBar = (i) => paddingLeft + i * groupWidth + (groupWidth - barWidth) / 2;
    const yFor = (value) => chartHeight - (value / maxPieces) * chartHeight;
    const hFor = (value) => (value / maxPieces) * chartHeight;

    return (
      <div className="bg-gray-50 rounded-lg p-4 mt-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium text-sm text-gray-700">Daily Pieces Sold</h4>
          <div className="flex items-center gap-1 text-xs">
            <div className="w-3 h-3 rounded-sm bg-emerald-400" />
            <span>Pieces</span>
          </div>
        </div>
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight + 30}`} className="w-full" style={{ height: 160 }}>
          {[0, 0.5, 1].map((pct, i) => (
            <g key={i}>
              <line x1={paddingLeft} y1={yFor(maxPieces * pct)} x2={chartWidth - paddingRight} y2={yFor(maxPieces * pct)} stroke="#E5E7EB" strokeWidth="1" />
              <text x={paddingLeft - 4} y={yFor(maxPieces * pct) + 4} textAnchor="end" fontSize="9" fill="#9CA3AF">
                {Math.round(maxPieces * pct)}
              </text>
            </g>
          ))}
          {dailyData.map((d, i) => (
            <g key={i}>
              <rect x={xForBar(i)} y={yFor(d.pieces)} width={barWidth} height={hFor(d.pieces)} fill="#34D399" rx="2" opacity="0.85">
                <title>{d.date}: {d.pieces} Pcs</title>
              </rect>
              <text x={xForBar(i) + barWidth / 2} y={chartHeight + 18} textAnchor="middle" fontSize="9" fill="#6B7280">
                {new Date(d.date).getDate()}/{new Date(d.date).getMonth() + 1}
              </text>
            </g>
          ))}
          <line x1={paddingLeft} y1={chartHeight} x2={chartWidth - paddingRight} y2={chartHeight} stroke="#D1D5DB" strokeWidth="1" />
        </svg>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-full">
              <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Revenue & Pieces</p>
              <p className="text-lg font-bold text-gray-900">₹{salesData?.shirts?.total?.toLocaleString()} | {salesData?.shirts?.pieces} Pcs</p>
              <p className="text-sm text-green-600">+{salesData?.shirts?.growth}% growth</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-full">
              <i className="ri-shopping-bag-line text-2xl text-blue-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Orders</p>
              <p className="text-2xl font-bold text-gray-900">{salesData?.shirts?.orders}</p>
              <p className="text-sm text-green-600">Shirts orders</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-full">
              <i className="ri-calculator-line text-2xl text-yellow-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Order Value</p>
              <p className="text-2xl font-bold text-gray-900">₹{salesData?.shirts?.avgOrder?.toLocaleString()}</p>
              <p className="text-sm text-gray-600">Per order</p>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Shirts Sales Performance</h3>
        {renderBarChart()}
        {renderPiecesChart()}
      </Card>
    </div>
  );
}

export default ShirtsSalesTab
